import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Hash, Phone, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext";

interface OrderCardProps {
  orderId: string;
  items: { name: string; quantity: number; customerNote?: string, allergies?: string }[];
  total: number;
  tip?: number;
  paymentMethod: 'online' | 'cash';
  paymentStatus?: string;
  stripePaymentIntentId?: string;
  status: 'new' | 'preparing' | 'ready' | 'completed' | 'cancelled';
  createdAt: Date;
  customerNote?: string;
  allergies?: string;
  customerName?: string;
  customerPhone?: string;
  tableNumber?: string;
  onStatusChange?: (orderId: string, newStatus: string) => void;
  onCancel?: (orderId: string) => void;
}

export function OrderCard({
  orderId,
  items,
  total,
  tip,
  paymentMethod,
  paymentStatus,
  status,
  createdAt,
  customerNote,
  allergies,
  customerName,
  customerPhone,
  tableNumber,
  onStatusChange,
  onCancel,
}: OrderCardProps) {
  const { formatCurrency } = useLanguage();
  const statusColors: Record<string, string> = {
    new: 'bg-blue-500',
    preparing: 'bg-yellow-500',
    ready: 'bg-green-500',
    completed: 'bg-gray-500',
    cancelled: 'bg-red-500',
  };

  const getNextStatus = () => {
    // For cash payments, only stop at 'ready' if not yet paid
    // If it's paid, it can proceed to 'completed'
    if (paymentMethod === 'cash' && status === 'ready' && paymentStatus !== 'paid' && paymentStatus !== 'completed') {
      return null; // No next status button for unpaid cash orders that are ready
    }

    const statusFlow = ['new', 'preparing', 'ready', 'completed'];
    const currentIndex = statusFlow.indexOf(status);
    if (currentIndex < statusFlow.length - 1) {
      return statusFlow[currentIndex + 1];
    }
    return null;
  };

  const nextStatus = getNextStatus();

  return (
    <Card className="p-4" data-testid={`card-order-${orderId}`}>
      <div className="flex flex-col gap-1 mb-4 pb-3 border-b border-muted">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-bold text-lg leading-none" data-testid={`text-order-id-${orderId}`}>
            Order #{orderId.slice(-8).toUpperCase()}
          </h4>
          <Badge className={`${statusColors[status]} font-bold px-3 py-0.5 shadow-sm shrink-0`} data-testid={`badge-status-${orderId}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        </div>

        <div className="flex items-center gap-1.5 mt-1 text-[10px] uppercase font-bold tracking-wider text-muted-foreground/60">
          <Clock className="h-3 w-3" />
          {formatDistanceToNow(createdAt, { addSuffix: true })}
        </div>

        <div className="space-y-1 mt-1">
          {tableNumber && (
            <div className="flex items-center gap-1.5 text-sm font-semibold text-primary" data-testid={`badge-table-${orderId}`}>
              <Hash className="h-3.5 w-3.5" />
              <span>Table {tableNumber}</span>
            </div>
          )}

          {customerName && (
            <div className="text-sm font-medium mt-0.5" data-testid={`text-customer-name-${orderId}`}>
              {customerName}
            </div>
          )}

          {customerPhone && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid={`text-customer-phone-${orderId}`}>
              <Phone className="h-3.5 w-3.5 text-primary" />
              <span>{customerPhone}</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2 mb-3">
        {items.map((item, index) => (
          <div key={index} className="space-y-1">
            <div className="text-sm">
              <span className="font-medium">{item.quantity}x</span> {item.name}
            </div>
            {item.customerNote && (
              <p className="text-xs text-muted-foreground italic pl-6" data-testid={`text-item-note-${index}`}>
                Note: {item.customerNote}
              </p>
            )}
            {item.allergies && (
              <p className="text-xs text-red-500 font-bold pl-6" data-testid={`text-item-allergies-${index}`}>
                ⚠️ Allergy: {item.allergies}
              </p>
            )}
          </div>
        ))}
      </div>

      {customerNote && (
        <div className="bg-muted p-2 rounded-md mb-2">
          <p className="text-sm font-bold text-gray-700 dark:text-gray-300">Note: {customerNote}</p>
        </div>
      )}

      {allergies && (
        <div className="bg-red-50 dark:bg-red-950/30 p-2 border border-red-200 dark:border-red-800 rounded-md mb-3">
          <p className="text-sm font-black text-red-600 dark:text-red-400">🚨 ALLERGIES: {allergies}</p>
        </div>
      )}

      <div className="flex items-center justify-between pt-3 border-t">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <span className="font-semibold" data-testid={`text-order-total-${orderId}`}>{formatCurrency(total)}</span>
          </div>
          {tip && tip > 0 && (
            <span className="text-xs text-muted-foreground" data-testid={`text-order-tip-${orderId}`}>
              Includes {formatCurrency(tip)} tip
            </span>
          )}
        </div>
        <Badge variant={paymentMethod === 'online' ? 'default' : 'outline'} data-testid={`badge-payment-${orderId}`}>
          {paymentMethod === 'online' ? 'Paid Online' : 'Register'}
        </Badge>
      </div>

      {onStatusChange && nextStatus && status !== 'cancelled' && (
        <div className="mt-3 pt-3 border-t">
          <Button
            size="sm"
            className="w-full"
            onClick={() => onStatusChange(orderId, nextStatus)}
            data-testid={`button-update-status-${orderId}`}
          >
            Mark as {nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)}
          </Button>
        </div>
      )}

      {/* Cancel Order Button */}
      {onCancel && status !== 'completed' && status !== 'cancelled' && (
        <div className="mt-2">
          <Button
            size="sm"
            variant="destructive"
            className="w-full gap-2"
            onClick={() => {
              if (window.confirm(
                paymentMethod === 'online'
                  ? `Cancel this order and issue a full refund of ${formatCurrency(total)}?\n\nThis will refund the customer's payment.`
                  : 'Cancel this order?\n\nThis cannot be undone.'
              )) {
                onCancel(orderId);
              }
            }}
            data-testid={`button-cancel-order-${orderId}`}
          >
            <X className="h-4 w-4" />
            Cancel Order{paymentMethod === 'online' ? ' & Refund' : ''}
          </Button>
        </div>
      )}
    </Card>
  );
}
